import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import debounce from "lodash/debounce";

type OnChangeFunc<T> = (newVal: T) => void;
export const useDebouncedState = <T>({
  delay = 1000,
  ...props
}: {
  value: T;
  onChange: OnChangeFunc<T>;
  delay?: number;
}) => {
  const [internalValue, setInternalValue] = useState(props.value);
  const onChangeRef = useRef<OnChangeFunc<T> | undefined>();
  // Always keep in sync
  onChangeRef.current = props.onChange;

  useLayoutEffect(() => setInternalValue(props.value), [props.value]);

  const debouncedOnChange = useMemo(() => debounce((newVal: T) => onChangeRef.current?.(newVal), delay), [delay]);
  const onChange = useCallback(
    (newVal: T) => {
      setInternalValue(newVal);
      debouncedOnChange(newVal);
    },
    [debouncedOnChange],
  );

  return [internalValue, onChange, debouncedOnChange.flush] as const;
};
